import { useQuery } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle2,
  Sparkles,
  Loader2,
  Gauge,
  Activity,
  Layers,
  History,
  CalendarDays,
  Hash,
  Info,
  Infinity as InfinityIcon,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { Breadcrumb } from '../../components/Breadcrumb'
import { EmptyStateBanner } from '../../components/EmptyStateBanner'
import { aiUsageResponse } from '../../lib/types'
import { formatDateTime, formatNumber, formatPattern } from '../../lib/format'
import { useChartHover } from '../../components/charts/use-chart-hover'
import { ChartTooltip } from '../../components/charts/ChartTooltip'

function fmtMonthYear(iso) {
  return formatPattern(iso, 'MMMM yyyy')
}

// Relative "… ago" phrasing. Used ONLY for the hover tooltip — the row
// itself always shows the absolute date/time (formatDateTime) so every
// entry in the list reads in one consistent format. Covers the full range
// so the tooltip stays meaningful for old calls too.
function fmtRelative(iso) {
  const d = new Date(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH} ${diffH === 1 ? 'hour' : 'hours'} ago`
  const diffD = Math.round(diffH / 24)
  if (diffD < 7) return `${diffD} ${diffD === 1 ? 'day' : 'days'} ago`
  if (diffD < 30) {
    const diffW = Math.round(diffD / 7)
    return `${diffW} ${diffW === 1 ? 'week' : 'weeks'} ago`
  }
  if (diffD < 365) {
    const diffMo = Math.round(diffD / 30)
    return `${diffMo} ${diffMo === 1 ? 'month' : 'months'} ago`
  }
  const diffY = Math.round(diffD / 365)
  return `${diffY} ${diffY === 1 ? 'year' : 'years'} ago`
}

function formatKind(raw) {
  if (!raw) return '—'
  return raw
    .split(/[_-]/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')
}

// ── Tier 1 outcome classification (TEMPORARY — single source of truth) ─
// The backend currently ships only `succeeded` (did the AI model produce
// the output) and `provider` (`'stub'` when the built-in generator
// produced it instead). Everything that needs to know "did the operation
// succeed / was it a fallback / did it fail" goes through classifyCall()
// so the temporary inference lives in exactly ONE place.
//
// TODO (Tier 2): once the backend records an explicit `operationOutcome`
// on each AiUsageEvent, replace the bodies below with it and delete the
// provider sniffing:
//     operationSucceeded = operationOutcome === 'SUCCESS' || operationOutcome === 'FALLBACK'
//     isFallback         = operationOutcome === 'FALLBACK'
//     isFailure          = operationOutcome === 'FAILED'
// The `provider === 'stub'` assumption holds ONLY while 'stub' is the
// sole fallback engine — a future local-LLM / cached / Azure fallback
// would break it, which is exactly why it is centralised here.
const FALLBACK_PROVIDER = 'stub'

function classifyCall({ succeeded, provider } = {}) {
  const isFallback = !succeeded && provider === FALLBACK_PROVIDER
  const isFailure = !succeeded && !isFallback
  return {
    // Business outcome — did the user get a usable result?
    operationSucceeded: Boolean(succeeded) || isFallback,
    // The configured AI model produced the result directly.
    isAiSuccess: Boolean(succeeded),
    // A built-in fallback engine produced the result (still a success).
    isFallback,
    // No usable result recorded, and not a recognised fallback. The
    // frontend can't know WHY (timeout, quota, firewall, parse error…),
    // so callers must not assert a specific cause.
    isFailure,
  }
}

/** Friendly provider label for a Recent Calls row. Business dashboards
 *  get a human label; internal engine ids (e.g. `paragraph-splitter@1`)
 *  belong in logs, not here — so a fallback always reads as the generic
 *  "Built-in Generator". Real AI providers keep their model name, which
 *  is meaningful to an admin choosing / monitoring a model.
 *
 *  TODO (Tier 2): key the fallback branch off the explicit backend
 *  outcome instead of the provider (see classifyCall). */
function describeProvider({ provider, model } = {}) {
  if (provider === FALLBACK_PROVIDER) return 'Built-in Generator'
  return model || provider || '—'
}

export function AiUsagePage() {
  const usageQuery = useQuery({
    queryKey: ['ai-usage'],
    queryFn: async () => {
      const res = await api.get('/ai-usage')
      return aiUsageResponse.parse(res.data)
    },
    refetchInterval: 60_000,
  })

  return (
    <div>
      <Breadcrumb current="AI Usage" />

      <header className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Sparkles size={20} className="text-[color:var(--color-accent-strong)]" />
          AI Usage
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          See how much AI your team uses.
        </p>
      </header>

      {usageQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </p>
      ) : usageQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(usageQuery.error).message}
        </p>
      ) : usageQuery.data ? (
        <Body data={usageQuery.data} />
      ) : null}
    </div>
  )
}

function Body({ data }) {
  // `quota === null` means the tenant's plan has unlimited AI — UI
  // renders "Unlimited" everywhere instead of "x% of N tokens".
  const unlimited = data.quota === null
  const overQuota = !unlimited && data.quotaUsedRate >= 100
  const nearQuota = !unlimited && data.quotaUsedRate >= 80 && !overQuota
  const remaining = unlimited
    ? null
    : Math.max(0, data.quota - data.currentMonthTotal)
  // Plan display name for the Plan card + unlimited usage summary.
  // `formatKind` turns a slug like `growth_annual` into `Growth Annual`.
  const planLabel = data.planTier ? formatKind(data.planTier) : null
  // ── AI Usage outcome metrics — Tier 1 (frontend-only) ────────────────
  // BUSINESS success vs AI-MODEL success. The Tier-1 fallback inference
  // (`provider === 'stub'`) is isolated in ONE place — classifyCall()
  // at module scope, above — so a future explicit backend outcome can
  // replace it in a single edit. See the TODO there.
  const recentTotal = data.recent.length
  // Business Success: the operation delivered a usable result — a direct
  // AI response OR a built-in fallback both count (the user got output).
  const businessOkCount = data.recent.filter(
    (r) => classifyCall(r).operationSucceeded,
  ).length
  const businessSuccessRate =
    recentTotal > 0 ? Math.round((businessOkCount / recentTotal) * 100) : null
  // AI Model Success: did the configured AI provider generate the result
  // directly, without the fallback engine? Raw telemetry (`succeeded`),
  // no inference — this is the engineering / health signal.
  const modelOkCount = data.recent.filter((r) => r.succeeded).length
  const modelSuccessRate =
    recentTotal > 0 ? Math.round((modelOkCount / recentTotal) * 100) : null

  const tone = unlimited
    ? 'success'
    : overQuota
      ? 'danger'
      : nearQuota
        ? 'warning'
        : 'success'

  return (
    <>
      {/* ── KPI strip ─────────────────────────────────────────────────
       *  Row 1 is usage/plan context. On a CAPPED plan that's the quota
       *  pair (Quota Used + Remaining); on an UNLIMITED plan those measure
       *  progress toward "no limit", which is meaningless — so we swap in
       *  a single Plan card instead. Row 2 is always the three outcome
       *  metrics, on every plan. */}
      <div
        className={
          'grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 ' +
          (unlimited ? 'lg:grid-cols-2' : 'lg:grid-cols-3')
        }
      >
        <KpiTile
          icon={Activity}
          label="Used This Month"
          value={formatNumber(data.currentMonthTotal)}
          caption={
            unlimited
              ? 'tokens (unlimited plan)'
              : `of ${formatNumber(data.quota)} tokens`
          }
          tone="sky"
        />
        {unlimited ? (
          <KpiTile
            icon={InfinityIcon}
            label="Plan"
            value={planLabel ?? 'Current plan'}
            caption="Unlimited AI usage"
            tone="violet"
            valueClassName="text-2xl"
          />
        ) : (
          <>
            <KpiTile
              icon={Gauge}
              label="Quota Used"
              value={`${data.quotaUsedRate}%`}
              caption={
                overQuota
                  ? 'Over quota'
                  : nearQuota
                    ? 'Nearing limit'
                    : 'Comfortable'
              }
              tone={tone}
            />
            <KpiTile
              icon={Layers}
              label="Remaining"
              value={formatNumber(remaining)}
              caption="tokens left this cycle"
              tone="violet"
            />
          </>
        )}
      </div>
      {/* Outcome metrics — always three, on every plan. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {/* Business Success — did the operation deliver a usable result?
         *  A built-in fallback counts as success (the user still got their
         *  output). This is what the old single "Success Rate" card should
         *  have shown instead of the AI-model number. */}
        <KpiTile
          icon={CheckCircle2}
          label="Success Rate"
          value={businessSuccessRate === null ? '—' : `${businessSuccessRate}%`}
          caption={
            businessSuccessRate === null
              ? 'No recent calls yet'
              : 'Successful task generations'
          }
          tone="emerald"
        />
        {/* AI Model Success — did the configured AI provider generate the
         *  result directly, without the fallback engine? 0% here is NOT a
         *  failure; it means the built-in generator covered the request. */}
        <KpiTile
          icon={Sparkles}
          label="AI Model Success"
          value={modelSuccessRate === null ? '—' : `${modelSuccessRate}%`}
          caption={
            modelSuccessRate === null
              ? 'No recent calls yet'
              : 'Generated directly by the AI model'
          }
          tone="sky"
          tooltip="Measures how often the configured AI model generated the response without using the built-in fallback engine."
        />
        <KpiTile
          icon={Hash}
          label="Total AI Calls"
          value={formatNumber(Number(data.currentMonthCalls ?? 0))}
          caption="AI calls this month"
          tone="violet"
        />
      </div>

      {/* ── Plan usage ────────────────────────────────────────────────
       *  A progress bar measures progress toward a finite cap. On an
       *  unlimited plan there is no cap, so an always-empty bar is
       *  misleading (it implies a quota waiting to be filled). Unlimited
       *  plans get a plain usage summary; capped plans keep the real bar
       *  plus the near/over-quota banners. */}
      {unlimited ? (
        <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 mb-6">
          <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
            Monthly AI usage
          </p>
          <div className="mt-2 flex items-center gap-2">
            <InfinityIcon
              size={22}
              className="text-[color:var(--color-accent-strong)]"
            />
            <span className="font-display text-2xl text-[color:var(--color-ink-strong)]">
              Unlimited
            </span>
          </div>
          <p className="mt-2 text-sm text-[color:var(--color-ink-muted)]">
            Your {planLabel ?? 'current'} plan has no monthly AI cap.{' '}
            <span className="font-medium text-[color:var(--color-ink-strong)] tabular-nums">
              {formatNumber(data.currentMonthTotal)} tokens
            </span>{' '}
            used so far this month.
          </p>
        </section>
      ) : (
        <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 mb-6">
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
                Monthly quota
              </p>
              <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                Tokens reset on the 1st of every month.
              </p>
            </div>
            <p className="text-sm text-[color:var(--color-ink-muted)] tabular-nums">
              {`${formatNumber(data.currentMonthTotal)} / ${formatNumber(data.quota)} tokens`}
            </p>
          </div>
          <div className="h-2.5 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
            <div
              className={
                'h-full rounded-full transition-all ' +
                (tone === 'danger'
                  ? 'bg-[color:var(--color-danger)]'
                  : tone === 'warning'
                    ? 'bg-[color:var(--color-warning)]'
                    : 'bg-[color:var(--color-accent-strong)]')
              }
              style={{ width: `${Math.min(data.quotaUsedRate, 100)}%` }}
            />
          </div>
          {overQuota ? (
            <p className="mt-4 text-sm font-medium text-[color:var(--color-danger)] inline-flex items-center gap-2">
              <AlertCircle size={14} />
              Over quota — new AI calls may be blocked or fall back to the
              heuristic generator. Contact your account manager to upgrade.
            </p>
          ) : nearQuota ? (
            <p className="mt-4 text-sm font-medium text-[color:var(--color-warning)] inline-flex items-center gap-2">
              <AlertCircle size={14} />
              Approaching the monthly quota.
            </p>
          ) : null}
        </section>
      )}

      {/* ── This-week bar chart (Sun→Sat) ─────────────────────────── */}
      <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 mb-6">
        <div className="flex items-center gap-2 mb-5">
          <span className="h-9 w-9 rounded-lg bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] flex items-center justify-center">
            <CalendarDays size={16} />
          </span>
          <div>
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
              This Week
            </h2>
            <p className="text-xs text-[color:var(--color-ink-muted)]">
              Calls per day, Sunday to Saturday — hover a bar for the exact count.
            </p>
          </div>
        </div>
        <WeeklyBars rows={data.byWeek ?? []} />
      </section>

      {/* ── By kind + Recent calls ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <section className="lg:col-span-2 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-center gap-2 mb-5">
            <span className="h-9 w-9 rounded-lg bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] flex items-center justify-center dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10">
              <Layers size={16} />
            </span>
            <div>
              <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
                By Type
              </h2>
              <p className="text-xs text-[color:var(--color-ink-muted)]">
                {fmtMonthYear(new Date().toISOString())}
              </p>
            </div>
          </div>
          {data.byKind.length === 0 ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] italic text-center py-8">
              No AI calls this month.
            </p>
          ) : (
            <ul className="space-y-3">
              {data.byKind.map((k) => {
                // Each `byKind` row counts CALLS, so the denominator
                // is the total calls this month — NOT
                // `currentMonthTotal` (which is now expressed in
                // tokens to match the plan-based quota).
                const callsTotal = data.currentMonthCalls ?? 0
                const pct =
                  callsTotal > 0
                    ? Math.round((k.total / callsTotal) * 100)
                    : 0
                return (
                  <li key={k.kind}>
                    <div className="flex items-baseline justify-between mb-1">
                      <p className="text-sm text-[color:var(--color-ink-strong)] font-medium">
                        {formatKind(k.kind)}
                      </p>
                      <p className="text-xs text-[color:var(--color-ink-muted)] tabular-nums">
                        {formatNumber(k.total)}{' '}
                        <span className="text-[color:var(--color-ink-subtle)]">
                          · {pct}%
                        </span>
                      </p>
                    </div>
                    <div className="h-2 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[color:var(--color-accent-strong)]"
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <section className="lg:col-span-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-center gap-2 mb-5">
            <span className="h-9 w-9 rounded-lg bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] flex items-center justify-center">
              <History size={16} />
            </span>
            <div>
              <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
                Recent Calls
              </h2>
              <p className="text-xs text-[color:var(--color-ink-muted)]">
                Latest activity across the workspace.
              </p>
            </div>
          </div>
          {data.recent.length === 0 ? (
            <EmptyStateBanner
              icon={Sparkles}
              title="No AI calls recorded yet"
              body="The next AI-assisted task breakdown, SOP upload, or workspace template will show up here."
              tone="violet"
              compact
            />
          ) : (
            // Cap the list at a fixed height and put the scrollbar
            // INSIDE this section. Without the cap the list grows
            // unbounded as more AI calls accumulate, pushing the
            // page footer off-screen and forcing the user to scroll
            // the whole page to see the bottom of "Recent Calls".
            // The card chrome (border, padding, header) stays
            // visible while the user scans down the recent activity.
            //
            // `pr-1` gives the scrollbar a small gutter so it
            // doesn't crowd the row content. `-mr-2` pulls the
            // gutter into the section's padding so the scrollbar
            // visually sits at the edge of the card, not inset by
            // an arbitrary amount.
            <ul className="divide-y divide-[color:var(--color-rule)] max-h-[480px] overflow-y-auto pr-1 -mr-2">
              {data.recent.map((r) => {
                // Single source of truth for the Tier-1 outcome (see
                // classifyCall). A fallback is a SUCCESS for the user, so
                // it must never read as a red error.
                const { isAiSuccess, isFallback, isFailure } = classifyCall(r)
                const StatusIcon = isFailure ? AlertCircle : CheckCircle2
                const iconTone = isAiSuccess
                  ? 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]'
                  : isFallback
                    ? 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]'
                    : 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]'
                return (
                  <li
                    key={r.id}
                    className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                  >
                    <span
                      className={
                        'h-8 w-8 rounded-full flex items-center justify-center shrink-0 ' +
                        iconTone
                      }
                    >
                      <StatusIcon size={14} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[color:var(--color-ink-strong)] font-medium truncate">
                        {formatKind(r.kind)}
                        {r.actorName ? (
                          <span className="text-[color:var(--color-ink-muted)] font-normal">
                            {' '}
                            · {r.actorName}
                          </span>
                        ) : null}
                      </p>
                      <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 tabular-nums">
                        <span
                          className="truncate"
                          title={
                            isFallback
                              ? 'Generated using the built-in fallback engine because the configured AI model did not return a usable response.'
                              : undefined
                          }
                        >
                          {describeProvider(r)}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span
                          className="cursor-help shrink-0"
                          title={fmtRelative(r.createdAt)}
                        >
                          {formatDateTime(r.createdAt)}
                        </span>
                      </p>
                    </div>
                    {isFallback ? (
                      <span
                        className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]"
                        title="Generated using the built-in fallback engine because the configured AI model did not return a usable response."
                      >
                        Built-in Fallback
                      </span>
                    ) : isFailure ? (
                      <span
                        className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]"
                        title="No usable response was recorded for this call. The exact cause is not available on this dashboard — check the logs."
                      >
                        No AI Response
                      </span>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </>
  )
}

/** Shared KPI tile — same shape as the Dashboard / MyTasks cards so this
 *  page reads as part of the same family. */
function KpiTile({
  icon: Icon,
  label,
  value,
  caption,
  tone = 'sky',
  tooltip = null,
  valueClassName = 'text-3xl',
}) {
  const toneMap = {
    sky: {
      bg: 'bg-[color:var(--color-info-bg)]',
      fg: 'text-[color:var(--color-info)]',
      border: 'border-[color:var(--color-info-border)]',
    },
    emerald: {
      bg: 'bg-[color:var(--color-success-bg)]',
      fg: 'text-[color:var(--color-success)]',
      border: 'border-[color:var(--color-success-border)]',
    },
    violet: {
      bg: 'bg-[color:var(--color-accent-bg)]',
      fg: 'text-[color:var(--color-accent-strong)]',
      border: 'border-[color:var(--color-accent-border)]',
    },
    warning: {
      bg: 'bg-[color:var(--color-warning-bg)]',
      fg: 'text-[color:var(--color-warning)]',
      border: 'border-[color:var(--color-warning-border)]',
    },
    danger: {
      bg: 'bg-[color:var(--color-danger-bg)]',
      fg: 'text-[color:var(--color-danger)]',
      border: 'border-[color:var(--color-danger-border)]',
    },
    success: {
      bg: 'bg-[color:var(--color-success-bg)]',
      fg: 'text-[color:var(--color-success)]',
      border: 'border-[color:var(--color-success-border)]',
    },
  }
  const t = toneMap[tone] ?? toneMap.sky
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute -top-12 -right-8 h-32 w-32 rounded-full blur-3xl opacity-40 ${t.bg}`}
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0 inline-flex items-center gap-1.5">
            {label}
            {tooltip ? (
              <span
                title={tooltip}
                aria-label={tooltip}
                className="cursor-help text-[color:var(--color-ink-subtle)]"
              >
                <Info size={13} />
              </span>
            ) : null}
          </h3>
          <p className={`mt-2 font-display ${valueClassName} text-[color:var(--color-ink-strong)] tabular-nums leading-none`}>
            {value}
          </p>
          <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">
            {caption}
          </p>
        </div>
        <span
          className={`h-12 w-12 rounded-xl border ${t.bg} ${t.fg} ${t.border} flex items-center justify-center shrink-0`}
        >
          <Icon size={24} />
        </span>
      </div>
    </div>
  )
}

/** Clean 12-month bars with a baseline grid line and value labels on hover. */
/** Build a dense 7-entry Sun→Sat array.
 *
 *  Backend `data.byWeek` is already shipped dense (the service pads
 *  it to all 7 slots, days with no activity carry `total: 0`). This
 *  helper is defensive — if the wire payload ever ships a sparse
 *  array (older API build, partial response, schema drift), we
 *  still render seven evenly-spaced columns.
 */
function buildSevenDaySeries(rows) {
  const LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const byDay = new Map()
  for (const r of rows ?? []) {
    if (typeof r?.dayIndex === 'number' && r.dayIndex >= 0 && r.dayIndex <= 6) {
      byDay.set(r.dayIndex, r)
    } else if (typeof r?.day === 'string') {
      const idx = LABELS.indexOf(r.day)
      if (idx >= 0) byDay.set(idx, r)
    }
  }
  return LABELS.map((label, idx) => {
    const existing = byDay.get(idx)
    return {
      day: label,
      dayIndex: idx,
      total: existing?.total ?? 0,
    }
  })
}

function WeeklyBars({ rows }) {
  // Dense 7-day Sun→Sat series. The backend ships it padded already
  // but `buildSevenDaySeries` is a defensive double-check so the
  // chart shape is invariant.
  const { ref, hover, track, clear } = useChartHover()
  const series = buildSevenDaySeries(rows)
  const max = Math.max(1, ...series.map((r) => r.total))
  return (
    <div ref={ref} className="relative">
      {/* Y-axis hint — top = max, bottom = 0. The label sits flush
       *  to the left edge so the bars don't share its column. */}
      <p className="absolute left-0 top-0 text-[10px] tabular-nums text-[color:var(--color-ink-subtle)]">
        {formatNumber(max)}
      </p>
      <p className="absolute left-0 bottom-7 text-[10px] tabular-nums text-[color:var(--color-ink-subtle)]">
        0
      </p>
      <div className="flex gap-3 h-44 pl-8 group">
        {series.map((d) => {
          const pct = (d.total / max) * 100
          return (
            <div
              key={d.dayIndex}
              className="flex-1 min-w-0 flex flex-col items-center transition-opacity duration-150 group-hover:opacity-40 hover:!opacity-100"
              onMouseEnter={track({
                color: 'var(--color-accent)',
                label: d.day,
                value: formatNumber(d.total),
              })}
              onMouseMove={track({
                color: 'var(--color-accent)',
                label: d.day,
                value: formatNumber(d.total),
              })}
              onMouseLeave={clear}
            >
              {/* Bar track fills the full height above the label so the
               *  bar's % height resolves against a DEFINITE box: the column
               *  stretches to h-44 (flex items-stretch) and this track
               *  flex-grows into it. Without a definite-height parent the %
               *  had nothing to resolve against and collapsed to the 6 px
               *  min — every bar became a flat sliver. Bars grow from the
               *  baseline; zero-activity days render NO bar so an empty day
               *  reads as empty and can't be mistaken for a tiny one. */}
              <div className="flex-1 w-full flex items-end">
                {d.total > 0 ? (
                  <div
                    className="w-full rounded-t-md transition-colors flex items-start justify-center pt-1 bg-[color:var(--color-accent-bg)] hover:bg-[color:var(--color-accent)] border-t-2 border-[color:var(--color-accent-strong)]"
                    style={{ height: `${Math.max(pct, 4)}%`, minHeight: '6px' }}
                  >
                    {pct > 40 ? (
                      <span className="text-[10px] font-semibold text-[color:var(--color-accent-strong)] tabular-nums">
                        {d.total}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <p className="text-[12px] text-[color:var(--color-ink-muted)] tabular-nums mt-1.5">
                {d.day}
              </p>
            </div>
          )
        })}
      </div>
      {hover ? <ChartTooltip {...hover} /> : null}
    </div>
  )
}
